import { ReactNode, useEffect, useState } from 'react'
import { composeClasses } from '@/utils/compose-classes'

export interface QuantitySelectorProps {
  defaultQuantity?: number
  value?: number
  maxQuantity?: number
  minQuantity?: number
  onChange?: (quantity: number) => void
  onChangeInputValue?: (quantity: number) => void
  leftContentButton?: ReactNode
  rightContentButton?: ReactNode
  classNameRightButton?: string
  classNameLeftButton?: string
  className?: string
  isEditable?: boolean
}

export const QuantitySelector = ({
  className,
  defaultQuantity,
  maxQuantity,
  minQuantity = 0,
  leftContentButton,
  rightContentButton,
  classNameRightButton,
  classNameLeftButton,
  onChange,
  onChangeInputValue,
  value,
  isEditable
}: QuantitySelectorProps) => {
  const [quantity, setQuantity] = useState(defaultQuantity ?? 0)

  const handleQuantityChange = (quantity: number) => {
    const newQuantity = Math.min(Math.max(quantity, minQuantity), quantity)
    setQuantity(newQuantity)
    if (onChange) {
      onChange(newQuantity)
    }
  }

  const maxLengthInput = maxQuantity?.toString().length ?? 3

  const handleChangeValue = (event: React.ChangeEvent<HTMLInputElement>) => {
    const cleanValueInput = event.target.value
      ?.replace(/[^0-9]/g, '')
      ?.slice(0, maxLengthInput)

    const inputValue = cleanValueInput ? parseInt(cleanValueInput, 10) : 0

    if (cleanValueInput === '' || isNaN(Number(cleanValueInput))) {
      setQuantity(minQuantity)
      return
    }

    let adjustedQuantity = Math.max(inputValue, minQuantity)

    if (maxQuantity) {
      adjustedQuantity = Math.min(adjustedQuantity, maxQuantity)
    }

    setQuantity(adjustedQuantity)
    if (onChangeInputValue) {
      onChangeInputValue(adjustedQuantity)
    }
  }

  useEffect(() => {
    if (value) {
      setQuantity(value)
    }
  }, [value])

  return (
    <div className={composeClasses(className, 'flex gap-2')}>
      <button
        type="button"
        className={composeClasses(
          classNameLeftButton,
          quantity <= minQuantity ? 'text-terciary' : 'text-primary',
          'rounded-full w-8 h-8 border border-surface-strokes flex items-center justify-center'
        )}
        disabled={quantity <= minQuantity}
        onClick={() => handleQuantityChange(quantity - 1)}
      >
        - {leftContentButton}
      </button>
      {isEditable ? (
        <input
          className="text-body-l input-number-format w-8 h-8 pb-[1px] rounded-lg bg-surface-gray-10 text-center flex items-center justify-center"
          value={quantity.toString()?.slice(0, maxLengthInput)}
          type="number"
          data-testid="input-editable"
          inputMode="numeric"
          onChange={handleChangeValue}
        />
      ) : (
        <div className="text-body-l w-8 h-8 rounded-lg bg-surface-gray-10 flex items-center justify-center">
          {quantity}
        </div>
      )}
      <button
        type="button"
        className={composeClasses(
          classNameRightButton,
          maxQuantity && quantity >= maxQuantity
            ? 'text-terciary'
            : 'text-primary',
          'rounded-full w-8 h-8 border border-surface-strokes flex items-center justify-center'
        )}
        disabled={maxQuantity ? quantity >= maxQuantity : false}
        onClick={() => handleQuantityChange(quantity + 1)}
      >
        + {rightContentButton}
      </button>
    </div>
  )
}

export default QuantitySelector
